/**
 * TutorStudentsScreen — l'onglet « Mes élèves » (`students`) : la liste et, au large, le cadre du live
 * (`<TutorStudentsDeskPage>`).
 *
 * La disposition se tranche sur la largeur de l'onglet, au seuil de la page (`DESK_FROM`, celui où le live ancre
 * la colonne du champion), lue comme elle la lit (le conteneur) :
 *  - en deçà, un écran à la fois : toucher un élève pousse sa fiche (`student-profil`, `withActions`), sa
 *    bulle (ses non-lus dedans) ouvre `messenger` (la fiche sur Messages ; l'ancienne ligne faisait `navigate("messenger")`) ;
 *  - au large, les trois colonnes du live (`useTutorStudentsDesk`) : toucher un élève le regarde, sa fiche au
 *    centre ; s'il a des non-lus, sa conversation s'ouvre à côté, dans la colonne du champion (sa pastille aussi).
 *
 * « À traiter » d'abord : les élèves dont des messages attendent sont en tête (`useTutorStudentsUnreadFirst`),
 * jusqu'à la relecture de la liste — au retour sur l'onglet, à « Relire ».
 *
 * « Activité commune » ouvre la génération d'aujourd'hui, le choix des élèves d'emblée.
 */
import { useNavigation } from "@react-navigation/native"
import React, { useCallback, useMemo, useState } from "react"
import { View } from "react-native"
import { useSelector } from "react-redux"

import { TutorStudentsDeskPage } from "../../../../../../design-system/tutor"
import { tutorStudentsViewModel } from "../../../../view-models/tutor/Students/tutorStudentsViewModel"
import { type UITutorStudent } from "../../../types/tutorEleves"
import { useTutorGenerateActivity } from "./useTutorGenerateActivity"
import { useTutorStudentsData } from "./useTutorStudentsData"
import { useTutorStudentsDesk } from "./useTutorStudentsDesk"
import { useTutorStudentsUnreadFirst } from "./useTutorStudentsUnreadFirst"

type StackNavigation = {
    push: (route: string, params?: object) => void
    navigate: (route: string, params?: object) => void
}

export function TutorStudentsScreen() {
    const navigation = useNavigation() as unknown as StackNavigation
    const vm = useSelector(tutorStudentsViewModel)
    const ordered = useTutorStudentsUnreadFirst(vm.students)
    const { refresh } = useTutorStudentsData(ordered.release)
    const generation = useTutorGenerateActivity()
    const [wide, setWide] = useState(false)
    const desk = useTutorStudentsDesk({ on: wide, students: vm.students, openGeneration: generation.open })

    const { select, openMessages } = desk
    const onOpenProfile = useCallback(
        (student: UITutorStudent) => {
            if (wide) select(student)
            else navigation.push("student-profil", { studentId: student.id, withActions: true })
        },
        [navigation, select, wide]
    )
    const onOpenMessages = useCallback(
        (student: UITutorStudent) => {
            if (wide) openMessages(student)
            else navigation.navigate("messenger", { studentId: student.id })
        },
        [navigation, openMessages, wide]
    )
    const openGeneration = generation.open
    const students = useMemo(
        () => ({
            students: ordered.students,
            selectedId: desk.selectedId,
            onOpenProfile,
            onOpenMessages,
            onGenerateForStudents: openGeneration != null ? () => openGeneration({ kind: "students" }) : undefined,
            refreshing: vm.refreshing,
            onRefresh: refresh,
            loading: vm.loading,
            error: vm.error,
            onRetry: refresh,
        }),
        [vm, ordered.students, desk.selectedId, onOpenProfile, onOpenMessages, openGeneration, refresh]
    )

    return (
        <View style={{ flex: 1 }}>
            <TutorStudentsDeskPage {...desk.page} students={students} onDeskChange={setWide} testID="tutor-students" />
            {desk.overlay}
            {generation.overlay}
        </View>
    )
}
